import React from 'react';
import _ from 'underscore';

import { SpaceHeaderTestIds } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';

import { Messages } from '../../../../codegen/Localize';
import { L10n } from '../../../../commonInfra/L10n/L10n';
import { IUserSettings } from '../../../../userSettings/models/IUserSettings';
import { SiteRoleOptionsResource } from '../../../services/resources/SiteRoleOptions';
import { IServerUserInfo } from '../../models/IServerUserInfo';
import { ISiteUserInfo } from '../../models/ISiteUserInfo';
import { SiteUserIcon } from './SiteUserIcon';

export interface UserSpaceHeaderProps {
  readonly item: IServerUserInfo | ISiteUserInfo;
  readonly inServerArea: boolean;
  readonly siteRole: string;
  readonly userSettings: IUserSettings;
  readonly isSessionUserSelf: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly isSessionSiteAdmin: boolean;
  readonly isServerDomainFamilyActiveDirectory: boolean;
  readonly siteRoleOptions: SiteRoleOptionsResource;
}

export function UserSpaceHeader(props: UserSpaceHeaderProps): JSX.Element {
  const {item} = props;
  return (
    <div>
      <div className={SpaceHeaderStyles.Row} data-tb-test-id={`tb-${item.contentType}-space-header-${item.id}`}>
        { renderSpaceIcon(item, props.isSessionUserSelf) }
        <div className={SpaceHeaderStyles.Column}>
          <div className={SpaceHeaderStyles.RowAlignedCenter}>
            <h1 data-tb-test-id={SpaceHeaderTestIds.name} className={SpaceHeaderStyles.Title}>{ item.displayName }</h1>
          </div>
          <div>
            <h2 className={SpaceHeaderStyles.Metadata}>
              <>
                <Username {...props} />
                <SiteRole {...props} />
                <LastSignedIn {...props} />
                <Email {...props} />
              </>
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderSpaceIcon(item: IServerUserInfo | ISiteUserInfo, isSessionUserSelf: boolean): JSX.Element {
  return <SiteUserIcon user={item} isSessionUserSelf={isSessionUserSelf} />;
}

function Username(props: UserSpaceHeaderProps): JSX.Element {
  let name = props.item.username;
  if (props.isServerDomainFamilyActiveDirectory || props.isSessionServerAdmin || props.isSessionSiteAdmin) {
    name = props.item.domainName + '\\' + name;
  }
  return (
    <>
      <span className={SpaceHeaderStyles.UserLabel}>
        {Messages.user()}
      </span>
      <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.username}>
        {name}
      </span>
    </>
  );
}

function SiteRole(props: UserSpaceHeaderProps): JSX.Element {
  if (!_.isUndefined(props.siteRole) && (props.isSessionUserSelf || props.isSessionServerAdmin || props.isSessionSiteAdmin)) {
    const label = props.inServerArea ? Messages.siteUserPlaceMaxSiteRole() : Messages.siteUserPlaceSiteRole();
    return (
      <>
        <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
          {label}
        </span>
        <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.siterole}>
          {props.siteRoleOptions.getSiteRoleName(props.siteRole)()}
        </span>
      </>
    );
  }

  return null;
}

function lastSignedInText(value: string): string {
  if(value){
    return L10n.localizedDateTime(value);
  } else {
    return Messages.never();
  }
}

function LastSignedIn(props: UserSpaceHeaderProps): JSX.Element {
  if (props.item.lastSignIn && (props.isSessionUserSelf || props.isSessionServerAdmin || props.isSessionSiteAdmin)) {
    return (
      <>
        <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
          {Messages.siteUserPlaceLastSignIn()}
        </span>
        <span className={SpaceHeaderStyles.MetadataInfo} data-tb-test-id={SpaceHeaderTestIds.lastSignedIn}>
          {lastSignedInText(props.item.lastSignIn)}
        </span>
      </>
    );
  }

  return null;
}

function Email(props: UserSpaceHeaderProps): JSX.Element {
  if (props.userSettings && props.userSettings.email) {
    return (
      <>
        <a
          className={SpaceHeaderStyles.userEmailHeaderLink}
          data-tb-test-id={SpaceHeaderTestIds.email}
          href={`mailto:${props.userSettings.email}`}
        >
          {props.userSettings.email}
        </a>
      </>
    );
  }

  return null;
}
